if (window.CarouselBase && !customElements.get('custom-projects')) {
  const CROSSFADE_MS = 450;

  // Swaps the active project between the left slot and the right column, and
  // drives the per-pin product mini-sliders (only one open at a time).
  class CustomProjects extends window.CarouselBase {
    connectedCallback() {
      this.activeSlot = this.querySelector('[data-slot="active"]');
      this.inactiveSlot = this.querySelector('[data-slot="inactive"]');

      // Blocks mode renders every card into the inactive slot; promote the first.
      if (this.activeSlot && this.inactiveSlot && !this.activeSlot.querySelector('.projects__card')) {
        const first = this.inactiveSlot.querySelector('.projects__card');
        if (first) this.activeSlot.appendChild(this.cardRoot(first));
      }

      this.cards = Array.from(this.querySelectorAll('.projects__card'));
      this.activeCard = this.querySelector('.projects__card.is-active') || this.cards[0];
      if (!this.activeCard) return;

      this.updateStates();
      this.initMinis();

      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);

      if (window.Shopify && window.Shopify.designMode) this.bindEditor();
    }

    initMinis() {
      this.querySelectorAll('.projects__mini').forEach((mini) => {
        const slides = this.slidesOf(mini);
        if (!slides.length) return;

        if (!slides.some((slide) => slide.classList.contains('is-active'))) {
          slides[0].classList.add('is-active');
        }
        slides.forEach((slide) => {
          slide.setAttribute('aria-hidden', slide.classList.contains('is-active') ? 'false' : 'true');
        });
        mini.querySelectorAll('.projects__mini-arrow').forEach((arrow) => {
          arrow.hidden = slides.length < 2;
        });
      });
    }

    teardown() {
      clearTimeout(this.swapTimer);
      (this.ghosts || []).forEach((ghost) => ghost.remove());
      this.removeEventListener('click', this.onClick);
      if (this.onBlockSelect) {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    onClick(event) {
      const swap = event.target.closest('.projects__swap');
      if (swap && !swap.disabled && this.contains(swap)) {
        this.activate(swap.closest('.projects__card'));
        return;
      }

      const trigger = event.target.closest('.projects__trigger');
      if (trigger && this.contains(trigger)) {
        this.toggleMini(trigger);
        return;
      }

      const arrow = event.target.closest('.projects__mini-arrow');
      if (arrow && this.contains(arrow)) {
        this.stepMini(arrow.closest('.projects__mini'), arrow.dataset.dir === 'next' ? 1 : -1);
        return;
      }

      // Any other click on the active card (photo, title, pin area) closes the mini.
      if (event.target.closest('.projects__mini')) return;
      if (event.target.closest('.projects__card.is-active')) this.closeMini();
    }

    activate(card, instant = false) {
      if (!card || card === this.activeCard || !this.inactiveSlot || this.swapping) return;

      const previous = this.activeCard;
      this.closeMini();

      if (instant || this.reducedMotion) {
        this.swapDom(card, previous);
        this.activeCard = card;
        this.updateStates();
        return;
      }

      // Snapshot both changing cards at their current boxes, swap the real
      // nodes into place instantly, then dissolve the snapshots — each region
      // crossfades where it sits instead of flying across the stage.
      const ghosts = [previous, card].map((el) => this.makeGhost(el));

      this.swapDom(card, previous);
      this.activeCard = card;
      this.updateStates();
      this.animateActive(card);

      this.playCrossfade(ghosts);
    }

    animateActive(card) {
      this.cards.forEach((c) => c.classList.remove('is-entering'));
      void card.offsetWidth; // restart the keyframe
      card.classList.add('is-entering');
    }

    // Theme blocks arrive wrapped in a .shopify-block div; move the wrapper.
    cardRoot(card) {
      const root = card.closest('.shopify-block');
      return root && this.contains(root) ? root : card;
    }

    swapDom(card, previous) {
      const marker = document.createComment('cp-slot');
      this.inactiveSlot.insertBefore(marker, this.cardRoot(card));
      this.activeSlot.appendChild(this.cardRoot(card));
      this.inactiveSlot.insertBefore(this.cardRoot(previous), marker);
      marker.remove();
    }

    makeGhost(el) {
      const rect = el.getBoundingClientRect();
      const ghost = el.cloneNode(true);
      ghost.classList.add('projects__card--ghost');
      ghost.removeAttribute('id');
      ghost.style.left = `${rect.left}px`;
      ghost.style.top = `${rect.top}px`;
      ghost.style.width = `${rect.width}px`;
      ghost.style.height = `${rect.height}px`;
      document.body.appendChild(ghost);
      return ghost;
    }

    playCrossfade(ghosts) {
      this.swapping = true;
      this.ghosts = ghosts;
      this.classList.add('is-swapping');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          ghosts.forEach((ghost) => {
            ghost.style.opacity = '0';
          });
        });
      });

      clearTimeout(this.swapTimer);
      this.swapTimer = setTimeout(() => this.endCrossfade(), CROSSFADE_MS + 60);
    }

    endCrossfade() {
      (this.ghosts || []).forEach((ghost) => ghost.remove());
      this.ghosts = null;
      this.classList.remove('is-swapping');
      this.swapping = false;
    }

    updateStates() {
      this.cards.forEach((card) => {
        const active = card === this.activeCard;
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-current', active ? 'true' : 'false');

        const swap = card.querySelector('.projects__swap');
        if (swap) {
          swap.disabled = active;
          if (active) swap.setAttribute('aria-hidden', 'true');
          else swap.removeAttribute('aria-hidden');
        }
      });
    }

    miniOf(trigger) {
      const card = trigger.closest('.projects__card');
      const id = trigger.getAttribute('aria-controls');
      return card && id ? card.querySelector(`#${CSS.escape(id)}`) : null;
    }

    triggerOf(mini) {
      const card = mini.closest('.projects__card');
      return card ? card.querySelector(`[aria-controls="${mini.id}"]`) : null;
    }

    slidesOf(mini) {
      return Array.from(mini.querySelectorAll('.projects__slide'));
    }

    toggleMini(trigger) {
      if (trigger.getAttribute('aria-expanded') === 'true') this.closeMini();
      else this.openMini(trigger);
    }

    openMini(trigger) {
      const mini = trigger && this.miniOf(trigger);
      if (!mini) return;
      this.closeMini();
      mini.classList.add('is-open');
      mini.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
    }

    closeMini() {
      this.querySelectorAll('.projects__mini.is-open').forEach((mini) => {
        mini.classList.remove('is-open');
        mini.setAttribute('aria-hidden', 'true');
      });
      this.querySelectorAll('.projects__trigger[aria-expanded="true"]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    }

    stepMini(mini, direction) {
      if (!mini) return;
      const slides = this.slidesOf(mini);
      if (slides.length < 2) return;
      const current = slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.selectSlide(mini, (current + direction + slides.length) % slides.length);
    }

    selectSlide(mini, index) {
      const track = mini.querySelector('.projects__mini-track');
      if (!track) return;
      this.slidesOf(mini).forEach((slide, i) => {
        const on = i === index;
        slide.classList.toggle('is-active', on);
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      track.style.setProperty('--cp-index', index);
    }

    // Theme editor: reveal whatever block the merchant selects so pin
    // coordinates and slides can be previewed in place.
    bindEditor() {
      this.onBlockSelect = (event) => {
        const el = event.target;
        if (!this.contains(el)) return;

        const card = el.closest('.projects__card');
        if (card) this.activate(card, true);

        const pin = el.closest('.projects__pin');
        if (pin) {
          this.openMini(pin.querySelector('.projects__trigger'));
          return;
        }

        const slide = el.closest('.projects__slide');
        if (slide) {
          const mini = slide.closest('.projects__mini');
          this.openMini(this.triggerOf(mini));
          this.selectSlide(mini, this.slidesOf(mini).indexOf(slide));
        }
      };
      this.onBlockDeselect = (event) => {
        if (this.contains(event.target) && event.target.closest('.projects__pin')) this.closeMini();
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  customElements.define('custom-projects', CustomProjects);
}
